// src/components/home/exercise-record/ExerciseDurationStat.jsx
import React, { useState, useEffect, useRef } from 'react';
import style from './ExerciseDurationStat.module.css';

const AnimatedNumber = ({ value, animate }) => {
    const [display, setDisplay] = useState(0);
    const rafRef = useRef(null);

    useEffect(() => {
        if (!animate) { setDisplay(0); return; }

        const duration = 1000;
        const start = performance.now();

        const tick = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
            setDisplay(Math.round(eased * value));
            if (progress < 1) rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }, [animate, value]);

    return <>{display.toLocaleString()}</>;
};

/**
 * 운동 시간 막대 그래프.
 * 목표(30분) 미달이면 빨강, 달성이면 파랑.
 * 소모 칼로리는 미노출 (2026.09.17)
 *
 * @param {{duration:{current:number, goal:number, percent:number, unit:string}}} data
 */
const ExerciseDurationStat = ({ data }) => {
    const { duration } = data;

    const wrapRef = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setVisible(true); },
            { threshold: 0.3 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const isAchieved = duration.percent >= 100;
    const barWidth = Math.min(duration.percent, 100);

    return (
        <div
            className={style.wrap}
            ref={wrapRef}
            data-status={isAchieved ? 'good' : 'warn'}
        >
            <div className={style.head}>
                <p className={style.label}>운동 시간</p>
                <p className={style.value}>
                    <strong>
                        <AnimatedNumber value={duration.current} animate={visible} />{duration.unit}
                    </strong>
                    <span> / {duration.goal}{duration.unit}</span>
                </p>
            </div>

            <div className={style.barRow}>
                <div className={style.barBg}>
                    <div
                        className={style.barFill}
                        style={{ width: visible ? `${barWidth}%` : '0%' }}
                    />
                </div>
                <span className={style.percent}>
                    <AnimatedNumber value={duration.percent} animate={visible} />%
                </span>
            </div>
        </div>
    );
};

export default ExerciseDurationStat;